/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: utils/languageHelpers.ts
 🗓️ Created: 2025-01-15
 🧩 Description:
    Language and sound prefix helpers for Extensions module.
────────────────────────────────────────────────────────────────*/

import { DEFAULT_LANGUAGE, DEFAULT_LANGUAGE_CODE, DEFAULT_VOICE } from '../../../data/formOptions';

/**
 * Maps language codes (ISO-REG) to FreeSWITCH sound directory paths
 */
export const SOUND_DIRECTORY_MAP: Record<string, string> = {
  'en-us': 'en/us',
  'es-es': 'es/es',
  'fr-fr': 'fr/fr',
  'de-de': 'de/de',
  'pt-pt': 'pt/pt',
  'it-it': 'it/it',
  'ru-ru': 'ru/ru',
  'zh-cn': 'zh/zh',
  'ja-jp': 'ja/ja',
  'ko-kr': 'ko/ko',
};

/**
 * Parses language payload string into language code and voice
 * @param value - Language string (e.g., "en-us-allison" or "en-us")
 * @returns Object with languageCode and voice
 */
export function parseLanguagePayload(value?: string): { languageCode: string; voice: string } {
  const fallback = DEFAULT_LANGUAGE.split('-');
  const parts = (value || DEFAULT_LANGUAGE).split('-').filter(Boolean);
  const lang = parts[0] && parts[1] ? `${parts[0]}-${parts[1]}` : `${fallback[0]}-${fallback[1]}`;
  const voice = parts[2] || fallback[2] || DEFAULT_VOICE;
  return {
    languageCode: lang || DEFAULT_LANGUAGE_CODE,
    voice: voice || DEFAULT_VOICE,
  };
}

/**
 * Builds sound prefix path from language code and voice
 * @param languageCode - Language code (e.g., "en-us")
 * @param voice - Voice name (e.g., "allison")
 * @returns Full path to sound directory
 */
export function buildSoundPrefixPath(languageCode: string, voice: string): string {
  if (!languageCode || !voice) return '';
  const normalizedLanguage = languageCode.toLowerCase();
  const languagePath = SOUND_DIRECTORY_MAP[normalizedLanguage] || SOUND_DIRECTORY_MAP['en-us'];
  // Return full path
  const base = '/usr/share/freeswitch/sounds';
  return `${base}/${languagePath}/${voice}/`;
}

/**
 * Gets default voicemail language from language code
 * @param languageCode - Language code (e.g., "en-us")
 * @returns Language prefix (e.g., "en")
 */
export function getDefaultVoicemailLanguage(languageCode: string): string {
  return languageCode.split('-')[0] || 'en';
}

